import { Button, Toast } from "@newsela/angelou";
import { useRouter } from "next/router";

import { objectTypes, actionNames, withTrackEvent } from "@newsela/snitch";
import useAssignmentDelete from "@/assignments/useAssignmentDelete";
import { handleAssignmentDelete } from "@/assignments/utils/assignmentActions";
import globalToasterRef from "@/base/globalToasterRef";
import { Assignment } from "@/types/models/Assignment.interface";
import useAssignmentReducerContext from "@/view/utils/useAssignmentReducerContext";
import { assignmentDeleteToastCss } from "./AssignmentDeleteButton.style";

interface DeleteButton {
  closeDeleteModal: () => void;
}

const ButtonWithTrackEvent = withTrackEvent(
  Button,
) as React.ForwardRefExoticComponent<
  React.ComponentProps<typeof Button> & React.RefAttributes<HTMLButtonElement>
>;

const AssignmentDeleteButton = ({ closeDeleteModal }: DeleteButton) => {
  const router = useRouter();
  const { pathname, query } = router;
  const contentId = query?.["levelId"];

  const [deleteAssignment] = useAssignmentDelete();
  const { dispatch, updatedAssignment } = useAssignmentReducerContext() as {
    dispatch: () => void;
    updatedAssignment: Pick<
      Assignment,
      "assignment_members" | "content_id" | "is_draft"
    >;
  };

  const assignmentMembers = updatedAssignment?.assignment_members;
  const deleteErrorMessage = "Assignment delete failed, please try again";
  const deleteSuccessMessage = "You have successfully deleted an assignment";

  const eventProperties = {
    objectType: objectTypes.assignment,
    actionName: actionNames.delete,
    streamProperties: {
      content_id: contentId,
      content_type: "Article",
      object_id: updatedAssignment?.content_id,
      content_ids: assignmentMembers?.map((member) =>
        "content" in member ? member.content.content_id : member.content_id,
      ),
      description: "Delete assignment",
    },
  };

  const ASSIGNMENT_DELETE_SUCCESS_TOAST_PROPS = {
    showIcon: true,
    statusColor: Toast.statusColor.success,
    children: deleteSuccessMessage,
    __cssFor: { root: assignmentDeleteToastCss },
  };
  const ASSIGNMENT_DELETE_ERROR_TOAST_PROPS = {
    showIcon: true,
    statusColor: Toast.statusColor.danger,
    children: deleteErrorMessage,
    __cssFor: { root: assignmentDeleteToastCss },
  };

  const removeSelectedAssignmentIdQueryParam = async () => {
    delete query?.["selectedAssignmentId"];
    await router.push({
      pathname,
      query,
    });
  };

  const handleDeleteAssignment = async () => {
    if (!updatedAssignment?.content_id) {
      closeDeleteModal();
      return;
    }

    try {
      await deleteAssignment(updatedAssignment.content_id);

      handleAssignmentDelete(dispatch);
      await removeSelectedAssignmentIdQueryParam();
      closeDeleteModal();
      globalToasterRef.current.displayToast(
        ASSIGNMENT_DELETE_SUCCESS_TOAST_PROPS,
      );
    } catch (_e) {
      globalToasterRef.current.displayToast(
        ASSIGNMENT_DELETE_ERROR_TOAST_PROPS,
      );
    }
  };

  return (
    <ButtonWithTrackEvent
      dataProps={{ "data-qa-selector": "modal-delete-assignment-button" }}
      flavor={Button.flavors.danger}
      onClick={() => {
        void (async () => {
          await handleDeleteAssignment();
        })();
      }}
      {...eventProperties}
    >
      Delete
    </ButtonWithTrackEvent>
  );
};
export default AssignmentDeleteButton;
